import { DragEndEvent, DndContext, useDroppable, useDraggable } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import clone = require("clone");
import React = require("react");
import { Survey, LanguageCode, Segmentation, fieldValidation } from "../../../../../../../amorg-common";
import { surveyValidation } from "../../../../../../../amorg-common/modules/fieldValidation/fields/surveyFieldValidation";
import { updateSurveySegmentation, deleteSurveySegmentation, moveSurveySegment } from "../../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../../designSystem/button/Button";
import { ButtonRow } from "../../../../../../../designSystem/button/ButtonRow";
import { Card } from "../../../../../../../designSystem/Card";
import { EditableText } from "../../../../../../../designSystem/compositeComponents/EditableText";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { DragButton } from "../../../../../../../designSystem/dragndrop/DragButton";
import { TextSaveField } from "../../../../../../../designSystem/formField/text/TextSaveField";
import { MessageCard } from "../../../../../../../designSystem/Message";

type SegmentationEditorProps = {
    survey:Survey,
    selectedSegmentationIndex: number,
    selectedLanguage:LanguageCode,
    onSegmentationsUpdated: () => void,
    onDeleted: () => void,
    onClose: () => void
}

export function SegmentationEditor(props: SegmentationEditorProps): JSX.Element {
    const [error, setError] = React.useState('');

    function getSelectedSegmentation():Segmentation
    {
        const seg = props.survey.segmentations[props.selectedSegmentationIndex];
        if(props.selectedLanguage===props.survey.languageCode)
            return {name:seg.name, values:seg.values};
        
        const trans = seg.translations.find(t => t.languageCode===props.selectedLanguage);
        if(!trans) throw new Error('Missing translation');
        return { name:trans.name, values:trans.values };
    }

    async function handleSaveSegmentName(value:string) {
        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        if(props.selectedLanguage===props.survey.languageCode)
            ss.name = value;
        else {
            const translation = ss.translations.find(t => t.languageCode===props.selectedLanguage);
            if(!translation) throw new Error('Missing translation');
            translation.name = value;
        }
        
        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('Navnet på segmentet er ikke gyldigt. Teksten skal være kort, og unik blandt alle segmenter, og må ikke overstige 50 tegn.');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            props.onSegmentationsUpdated();
        }
    }

    async function handleSaveSegmentValue(index: number, value: string) 
    {
        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        if(props.selectedLanguage===props.survey.languageCode)
            ss.values[index] = value;
        else {
            const translation = ss.translations.find(t => t.languageCode===props.selectedLanguage);
            if(!translation) throw new Error('Missing translation');
            translation.values[index] = value;
        }

        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('Fejl i segmentværdierne. Værdierne skal være unikke, må ikke være tomme og må ikke overstige 50 tegn.');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            props.onSegmentationsUpdated();
        }
    }

    async function handleClickAddValue() {

        const segmentation = props.survey.segmentations[props.selectedSegmentationIndex];

        let newCount = segmentation.values.length + 1;
        while(segmentation.values.some(s => 
            s === `værdi ${newCount}` || segmentation.translations.some(t => t.values.includes(`værdi ${newCount}`))
        )) {
            newCount++;
        }

        const newValue = `værdi ${newCount}`;

        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        ss.values.push(newValue);
        for(const t of ss.translations)
            t.values.push(newValue);

        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('Der opstod en uventet fejl. Prøv igen.');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            props.onSegmentationsUpdated();
        }
    }

    async function handleDeleteValue(index:number) {
        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        ss.values.splice(index, 1);
        for(const t of ss.translations)
            t.values.splice(index, 1);

        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('En segmentering skal indeholde mindst 2 værdier. Tilføj en ny værdi før du sletter de eksisterende.');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            props.onSegmentationsUpdated();
        }
    }

    async function handleDeleteSegmentation() {
        await deleteSurveySegmentation(props.survey.id, props.selectedSegmentationIndex);
        props.onDeleted();
    }

    async function handleMoveSegment(move: 'up' | 'down', valueIndex) 
    {
        const ss = getSelectedSegmentation();

        if (move === 'up') {
            if (valueIndex > 0) {

                await moveSurveySegment(props.survey.id, props.survey.segmentations, props.selectedSegmentationIndex, valueIndex, valueIndex - 1);
                props.onSegmentationsUpdated();
            }
        }
        if (move === 'down') {
            if (valueIndex < ss.values.length - 1) {
                await moveSurveySegment(props.survey.id, props.survey.segmentations, props.selectedSegmentationIndex, valueIndex, valueIndex + 1);
                props.onSegmentationsUpdated();
            }
        }
    }

    async function handleDragEnd(event: DragEndEvent)
    {
        const activeId = event.active.id.toString();
        const overId = event.over?.id.toString();

        if (!overId || activeId === overId)
            return;

        if (!activeId.startsWith('segment_value_') || !overId.startsWith('segment_value_'))
            return;

        const fromValueIndex = parseInt(activeId.substring(14));
        const toValueIndex = parseInt(overId.substring(14));

        if (fromValueIndex === toValueIndex)
            return;

        await moveSurveySegment(
            props.survey.id,
            props.survey.segmentations,
            props.selectedSegmentationIndex,
            fromValueIndex,
            toValueIndex
        );
        props.onSegmentationsUpdated();
    }

    const segmentation = getSelectedSegmentation();

    return (
        <>
            <Flex flexDirection="column" gap={16}>
                
                {error &&
                    <MessageCard variant="error">
                        {error}
                    </MessageCard>
                }

                <TextSaveField
                    label="Segmenteringsnavn"
                    placeholder="Ny segmentering"
                    value={segmentation.name}
                    onSave={(value, isValid)=>{ 
                        if(isValid)
                           handleSaveSegmentName(value);
                    }}
                    validator={(currentValue) => 
                        fieldValidation.survey.segmentationName(currentValue) 
                            ? {valid:true}
                            : {valid:false, errorMessage:`Ugyldig værdi. Skal udfyldes med maks. 50 (${currentValue.length}) tegn.`}
                    } />

                <div>
                    <DndContext onDragEnd={handleDragEnd}>
                        <Flex flexDirection="column" gap={2}>
                            {segmentation.values.map((value, index) =>
                                <SegmentValueRow
                                    key={index}
                                    index={index}
                                    value={value}
                                    onMoveSegment={handleMoveSegment}
                                    onSave={handleSaveSegmentValue}
                                    onDelete={handleDeleteValue}
                                />
                            )}
                        </Flex>
                    </DndContext>
            
                    <ButtonRow variant="right-aligned" marginTop={10}>
                        <Button variant="text"
                            onClick={handleClickAddValue}
                            icon="actions_plus"
                            text={"Tilføj ny værdi"} />
                    </ButtonRow>

                </div>

                <ButtonRow variant="space-between">
                    <Button variant="destructive"
                        text={"Slet denne segmentering"}
                        onClick={handleDeleteSegmentation}
                        icon={"actions_close"} />

                    <Button variant="filled"
                        onClick={props.onClose}
                        text={"Luk"} />
                </ButtonRow>
            </Flex>
        </>
    );
}

type SegmentValueRowProps = {
    index: number,
    value: string,
    onMoveSegment: (move: 'up' | 'down', valueIndex: number) => void,
    onSave: (index: number, value: string) => void,
    onDelete: (index: number) => void,
}

function SegmentValueRow(props: SegmentValueRowProps): JSX.Element
{
    const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
        id: `segment_value_${props.index}`,
    });

    const { attributes, listeners, setNodeRef: setDraggableNodeRef, setActivatorNodeRef, transform } = useDraggable({
        id: `segment_value_${props.index}`,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    return (
        <div
            ref={(node) => {
                setDroppableNodeRef(node);
                setDraggableNodeRef(node);
            }}
            {...attributes}
            style={{
                ...draggableStyle,
                backgroundColor: isOver ? '#f0f0f0' : undefined,
            }}>
            <Card variant="outlined" padding="4px">
                <Flex alignItems="center" gap={4}>
                    <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />
                    {/* <SegmentMove>
                        <div>
                            
                            <Button variant="text"
                                icon="arrows_chevron_chevronUp"
                                onClick={() => props.onMoveSegment('up', props.index)}
                                text={""} />
                            <Button variant="text"
                                icon="arrows_chevron_chevronDown"
                                onClick={() => props.onMoveSegment('down', props.index)}
                                text={""} />
                        </div>
                    </SegmentMove> */}
                    <EditableText
                        ariaLabel="Segmenteringsværdi"
                        placeholder="Ny værdi"
                        value={props.value}
                        onSave={(value) => props.onSave(props.index, value)} 
                        validator={(currentValue) => 
                            fieldValidation.survey.segmentationValue(currentValue)
                                ? {valid:true}
                                : {valid:false, errorMessage:`Ugyldig værdi. Skal udfyldes med maks. 50 (${currentValue.length}) tegn.`}
                            
                        } />
                    <Button variant="text"
                        icon="actions_close"
                        text={""}
                        onClick={() => props.onDelete(props.index)} />
                </Flex>
            </Card>
        </div>
    );
}